import { ApolloLink, Observable, Observer, Operation, NextLink } from '@apollo/client';

const MAX_RETRIES = 3;
const RETRY_INTERVAL = 1000; // ms

type Options = {
  shouldRetry: (b: Operation, a: any[]) => boolean;
};

class RetryableOperation {
  operation: Operation;
  forward: NextLink;
  retryCount: number;
  observer: Observer<any> | undefined | null;
  currentSubscription: Observable<any> | undefined | null;
  // @ts-expect-error ts-migrate(2304) FIXME: Cannot find name 'TimeoutID'.
  timerId: TimeoutID | undefined | null;
  shouldRetry: (b: Operation, a: any[]) => boolean;
  complete: boolean;

  constructor(
    operation: Operation,
    forward: NextLink,
    shouldRetry: (b: Operation, a: any[]) => boolean
  ) {
    this.operation = operation;
    this.forward = forward;
    this.retryCount = 0;
    this.observer = null;
    this.currentSubscription = null;
    this.timerId = null;
    this.shouldRetry = shouldRetry;
    this.complete = false;
  }

  subscribe(observer: Observer<any>) {
    this.observer = observer;
  }

  unsubscribe() {
    this.observer = null;
    if (this.currentSubscription) {
      // @ts-expect-error
      this.currentSubscription.unsubscribe();
      this.currentSubscription = null;
    }
    if (this.timerId) {
      clearTimeout(this.timerId);
    }
    this.timerId = null;
  }

  try() {
    // @ts-expect-error
    this.currentSubscription = this.forward(this.operation).subscribe({
      next: this.onNext,
      error: this.onError,
      complete: this.onComplete,
    });
  }

  onNext = (value: any) => {
    const { errors } = value;
    if (errors) {
      if (this.retryCount < MAX_RETRIES && this.shouldRetry(this.operation, errors)) {
        this.retryCount += 1;
        this.scheduleRetry(this.retryCount);
        return;
      }
    }
    this.complete = true;
    if (this.observer) {
      this.observer?.next?.(value);
    }
  };

  onError = (error: any) => {
    if (this.retryCount < MAX_RETRIES && this.shouldRetry(this.operation, [error])) {
      this.retryCount += 1;
      this.scheduleRetry(this.retryCount);
      return;
    }
    if (this.observer) {
      this.observer?.error?.(error);
    }
  };

  onComplete = () => {
    if (this.complete && this.observer) {
      this.observer?.complete?.();
    }
  };

  scheduleRetry(retryStep: number) {
    this.timerId = setTimeout(() => {
      this.timerId = null;
      this.try();
      // Do exponential backoff
    }, RETRY_INTERVAL * 2 ** retryStep + Math.random() * 1000);
  }
}

/* The retry link is responsible for retrying any
 * operations allowed by the shouldRetry predicate
 * function that failed because of a query or network error.
 *
 * const retryLink = RetryLink({
 *    shouldRetry: (operation, errors) => {
 *      const { operationName } = operation;
 *      if (operationName === 'ContentSourcesContent') {
 *        return errors.map(error => get(error, 'serviceMetadata.service_status')).indexOf(503) >= 0
 *      }
 *      return false;
 *    }
 * });
 *
 * Based off of:
 * https://github.com/apollographql/apollo-link/tree/master/packages/apollo-link-retry
 */
export class RetryLink extends ApolloLink {
  constructor(options: Options) {
    super();
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'options' does not exist on type 'RetryLi... Remove this comment to see the full error message
    this.options = options;
  }

  request(operation: any, forward: any) {
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'options' does not exist on type 'RetryLi... Remove this comment to see the full error message
    const { shouldRetry } = this.options;
    const retryable = new RetryableOperation(operation, forward, shouldRetry);
    return new Observable<any>((observer) => {
      retryable.subscribe(observer);
      retryable.try();
      return () => {
        retryable.unsubscribe();
      };
    });
  }
}
